import React, { useState } from 'react';
import {
  BuildingOfficeIcon,
  UsersIcon,
  CalendarIcon,
  TrashIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { CompanyService } from '../../services/companyService';
import { Company } from '../../types';

interface CompanyDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  company: Company | null;
  onDeleteSuccess?: () => void;
  onUpdateSuccess?: () => void;
}

const CompanyDetailModal: React.FC<CompanyDetailModalProps> = ({
  isOpen,
  onClose,
  company,
  onDeleteSuccess,
  onUpdateSuccess,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState<string>('');

  // 編集モード関連の状態
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState<any>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>('');

  const companyService = React.useMemo(() => new CompanyService(), []);

  if (!company) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusBadge = (subscriptionStatus: string) => {
    const isActive = subscriptionStatus === 'active' || subscriptionStatus === 'trial';
    const statusText = isActive ? 'アクティブ' : '無効';
    const statusColor = isActive
      ? 'bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-400'
      : 'bg-red-100 text-red-800 dark:bg-red-800/20 dark:text-red-400';

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColor}`}>
        {statusText}
      </span>
    );
  };

  const getSubscriptionPlanLabel = (plan: string) => {
    const plans: Record<string, string> = {
      basic: 'ベーシック',
      standard: 'スタンダード',
      premium: 'プレミアム',
      enterprise: 'エンタープライズ',
    };
    return plans[plan] || plan;
  };

  const handleDeleteCompany = async () => {
    try {
      setIsDeleting(true);
      setDeleteError('');
      await companyService.deleteCompany(company.id);
      setShowDeleteConfirm(false);
      onDeleteSuccess?.();
      onClose();
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : '削除に失敗しました');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditStart = () => {
    setEditData({
      name: company.name,
      code: company.code,
      contactPerson: company.contactPerson || '',
      subscriptionStatus: company.subscriptionStatus,
      address: company.address || '',
      phone: company.phone || '',
      email: company.email || '',
      subscriptionPlan: company.subscriptionPlan,
      maxUsers: company.maxUsers,
      maxDrivers: company.maxDrivers,
      maxVehicles: company.maxVehicles,
      settings: company.settings,
    });
    setIsEditMode(true);
    setSaveError('');
  };

  const handleEditCancel = () => {
    setIsEditMode(false);
    setEditData({});
    setSaveError('');
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveError('');
      await companyService.updateCompany(company.id, editData);
      onUpdateSuccess?.();
      onClose();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : '保存に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title='陸送会社詳細' size='lg'>
        <div className='space-y-6'>
          {/* 基本情報セクション */}
          <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-6'>
            <div className='flex items-center mb-4'>
              <BuildingOfficeIcon className='h-6 w-6 text-primary-600 mr-3' />
              <h3 className='text-lg font-medium text-gray-900 dark:text-white'>基本情報</h3>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  会社名
                </label>
                {isEditMode ? (
                  <input
                    type='text'
                    value={editData.name || ''}
                    onChange={e => setEditData({ ...editData, name: e.target.value })}
                    className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500'
                  />
                ) : (
                  <p className='text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-600 p-2 rounded'>
                    {company.name}
                  </p>
                )}
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  会社コード
                </label>
                {isEditMode ? (
                  <input
                    type='text'
                    value={editData.code || ''}
                    onChange={e => setEditData({ ...editData, code: e.target.value })}
                    className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono focus:outline-none focus:ring-2 focus:ring-primary-500'
                  />
                ) : (
                  <p className='text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-600 p-2 rounded font-mono'>
                    {company.code}
                  </p>
                )}
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  担当者
                </label>
                {isEditMode ? (
                  <input
                    type='text'
                    value={editData.contactPerson || ''}
                    onChange={e => setEditData({ ...editData, contactPerson: e.target.value })}
                    placeholder='担当者名を入力'
                    className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500'
                  />
                ) : (
                  <p className='text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-600 p-2 rounded'>
                    {company.contactPerson || '未設定'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* 連絡先情報セクション */}
          <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-6'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-4'>連絡先情報</h3>

            <div className='grid grid-cols-1 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  住所
                </label>
                {isEditMode ? (
                  <textarea
                    value={editData.address || ''}
                    onChange={e => setEditData({ ...editData, address: e.target.value })}
                    placeholder='住所を入力'
                    rows={3}
                    className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500'
                  />
                ) : (
                  <p className='text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-600 p-2 rounded'>
                    {company.address || '未設定'}
                  </p>
                )}
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                    電話番号
                  </label>
                  {isEditMode ? (
                    <input
                      type='tel'
                      value={editData.phone || ''}
                      onChange={e => setEditData({ ...editData, phone: e.target.value })}
                      placeholder='電話番号を入力'
                      className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500'
                    />
                  ) : (
                    <p className='text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-600 p-2 rounded'>
                      {company.phone || '未設定'}
                    </p>
                  )}
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                    メールアドレス
                  </label>
                  {isEditMode ? (
                    <input
                      type='email'
                      value={editData.email || ''}
                      onChange={e => setEditData({ ...editData, email: e.target.value })}
                      placeholder='メールアドレスを入力'
                      className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500'
                    />
                  ) : (
                    <p className='text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-600 p-2 rounded'>
                      {company.email || '未設定'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* プラン・制限情報セクション */}
          <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-6'>
            <div className='flex items-center mb-4'>
              <UsersIcon className='h-6 w-6 text-primary-600 mr-3' />
              <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
                プラン・制限情報
              </h3>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  サブスクリプションプラン
                </label>
                {isEditMode ? (
                  <select
                    value={editData.subscriptionPlan || ''}
                    onChange={e => setEditData({ ...editData, subscriptionPlan: e.target.value })}
                    className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500'
                  >
                    <option value='basic'>ベーシック</option>
                    <option value='standard'>スタンダード</option>
                    <option value='premium'>プレミアム</option>
                    <option value='enterprise'>エンタープライズ</option>
                  </select>
                ) : (
                  <p className='text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-600 p-2 rounded'>
                    {getSubscriptionPlanLabel(company.subscriptionPlan)}
                  </p>
                )}
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  サブスクリプション状態
                </label>
                {isEditMode ? (
                  <select
                    value={editData.subscriptionStatus || 'active'}
                    onChange={e => setEditData({ ...editData, subscriptionStatus: e.target.value })}
                    className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500'
                  >
                    <option value='active'>アクティブ</option>
                    <option value='trial'>トライアル</option>
                    <option value='inactive'>停止中</option>
                    <option value='suspended'>一時停止</option>
                  </select>
                ) : (
                  <div className='flex items-center'>
                    {getStatusBadge(company.subscriptionStatus)}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 日時情報セクション */}
          <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-6'>
            <div className='flex items-center mb-4'>
              <CalendarIcon className='h-6 w-6 text-primary-600 mr-3' />
              <h3 className='text-lg font-medium text-gray-900 dark:text-white'>日時情報</h3>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  作成日時
                </label>
                <p className='text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-600 p-2 rounded'>
                  {formatDate(company.createdAt)}
                </p>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  更新日時
                </label>
                <p className='text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-600 p-2 rounded'>
                  {formatDate(company.updatedAt)}
                </p>
              </div>
            </div>
          </div>

          {/* エラー表示 */}
          {(deleteError || saveError) && (
            <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4'>
              <p className='text-sm text-red-800 dark:text-red-200'>{deleteError || saveError}</p>
            </div>
          )}

          {/* アクションボタン */}
          <div className='flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700'>
            {isEditMode ? (
              <div className='flex space-x-3'>
                <Button variant='secondary' onClick={handleEditCancel} disabled={isSaving}>
                  キャンセル
                </Button>
                <Button variant='primary' onClick={handleSave} disabled={isSaving}>
                  {isSaving ? '保存中...' : '保存'}
                </Button>
              </div>
            ) : (
              <div className='flex space-x-3'>
                <Button variant='primary' onClick={handleEditStart} disabled={showDeleteConfirm}>
                  <PencilIcon className='h-4 w-4 mr-2' />
                  編集
                </Button>
                <Button
                  variant='danger'
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={showDeleteConfirm}
                >
                  <TrashIcon className='h-4 w-4 mr-2' />
                  削除
                </Button>
                <Button variant='secondary' onClick={onClose}>
                  閉じる
                </Button>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* 削除確認モーダル */}
      {showDeleteConfirm && (
        <div className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50'>
          <div className='bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6'>
            <div className='flex items-center mb-4'>
              <TrashIcon className='h-6 w-6 text-red-600 dark:text-red-400 mr-3' />
              <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>削除の確認</h3>
            </div>
            <p className='text-sm text-gray-600 dark:text-gray-300 mb-6'>
              会社「{company.name}」を削除しますか？この操作は取り消せません。
            </p>
            <div className='flex justify-end space-x-3'>
              <Button
                variant='secondary'
                onClick={() => setShowDeleteConfirm(false)}
                size='md'
                disabled={isDeleting}
              >
                キャンセル
              </Button>
              <Button
                variant='danger'
                onClick={handleDeleteCompany}
                size='md'
                disabled={isDeleting}
              >
                <TrashIcon className='h-4 w-4 mr-2' />
                {isDeleting ? '削除中...' : '削除する'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CompanyDetailModal;
